import React, { useEffect, useState } from 'react';
import { Veiculo } from '../../types/veiculo';
import { VeiculoChecklist, VeiculoAbastecimento } from '../../types/veiculoExtra';
import { veiculoExtraService } from '../../services/veiculoExtraService';
import {
  Truck,
  Wrench,
  CheckCircle2,
  Fuel,
  TrendingUp,
  AlertTriangle,
  ClipboardCheck,
  DollarSign,
  Gauge,
  UserCheck,
  Users
} from 'lucide-react';

interface Props {
  veiculos: Veiculo[];
  motoristasList: { id: number; nome: string }[];
  onOpenManutencao: (v: Veiculo) => void;
  onOpenChecklist: (v: Veiculo) => void;
  onOpenAbastecimento: (v: Veiculo) => void;
}

export const VeiculosDashboard: React.FC<Props> = ({
  veiculos,
  motoristasList,
  onOpenManutencao,
  onOpenChecklist,
  onOpenAbastecimento,
}) => {
  const [abastecimentos, setAbastecimentos] = useState<VeiculoAbastecimento[]>([]);
  const [checklists, setChecklists] = useState<VeiculoChecklist[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    setLoading(true);
    try {
      const [abs, chk] = await Promise.all([
        veiculoExtraService.getAbastecimentos(),
        veiculoExtraService.getChecklists(),
      ]);
      setAbastecimentos(abs);
      setChecklists(chk);
    } catch (e) {
      console.error('Erro ao carregar dados do dashboard de frota:', e);
    } finally {
      setLoading(false);
    }
  };

  // Separação rigorosa de Frota Própria vs Agregados
  const frotaPropria = veiculos.filter((v) => !v.agregado_id);
  const agregados = veiculos.filter((v) => !!v.agregado_id);

  const emManutencao = frotaPropria.filter((v) => v.status_frota === 'em_manutencao');
  const disponiveis = frotaPropria.filter(
    (v) => (v.status_frota === 'disponivel' || !v.status_frota) && v.ativo !== false
  );
  const emUso = frotaPropria.filter((v) => v.status_frota === 'em_uso');
  const inativosPropria = frotaPropria.filter((v) => v.ativo === false || v.status_frota === 'desativado');

  // Cálculos de KPIs Financeiros e de Consumo
  const totalGastoCombustivel = abastecimentos.reduce((acc, curr) => acc + (Number(curr.valor_total) || 0), 0);
  const totalLitrosAbastecidos = abastecimentos.reduce((acc, curr) => acc + (Number(curr.litros) || 0), 0);
  const totalKmPercorrido = abastecimentos.reduce((acc, curr) => acc + (Number(curr.km_percorrido) || 0), 0);

  const mediaKmLGeral =
    totalLitrosAbastecidos > 0 && totalKmPercorrido > 0
      ? (totalKmPercorrido / totalLitrosAbastecidos).toFixed(2)
      : '0.00';

  const custoMedioKmGeral =
    totalKmPercorrido > 0 && totalGastoCombustivel > 0
      ? (totalGastoCombustivel / totalKmPercorrido).toFixed(2)
      : '0.00';

  // Checklists métricas
  const totalChecklists = checklists.length;
  const checklistsAprovados = checklists.filter((c) => c.status_geral === 'aprovado').length;
  const checklistsAtencao = checklists.filter((c) => c.status_geral === 'com_observacoes' || c.status_geral === 'reprovado').length;

  return (
    <div className="space-y-6">
      {/* Top Banner KPI Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        {/* Total Frota Própria */}
        <div className="bg-white p-5 rounded-2xl border border-slate-200 shadow-sm relative overflow-hidden">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-xs font-bold text-slate-500 uppercase tracking-wider">Frota Própria</p>
              <h3 className="text-2xl font-black text-slate-900 mt-1 font-mono">{frotaPropria.length} <span className="text-xs font-normal text-slate-500">Veículos</span></h3>
            </div>
            <div className="p-3 bg-red-50 text-red-600 rounded-xl">
              <Truck className="w-6 h-6" />
            </div>
          </div>
          <div className="mt-3 text-xs text-slate-600 flex items-center gap-1.5 border-t border-slate-100 pt-2">
            <span className="font-bold text-emerald-600">{disponiveis.length} prontos</span> • <span className="font-bold text-amber-600">{emManutencao.length} na oficina</span>
          </div>
        </div>

        {/* Em Manutenção */}
        <div className="bg-white p-5 rounded-2xl border border-amber-200 shadow-sm relative overflow-hidden bg-amber-50/30">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-xs font-bold text-amber-800 uppercase tracking-wider">Em Manutenção</p>
              <h3 className="text-2xl font-black text-amber-900 mt-1 font-mono">{emManutencao.length} <span className="text-xs font-normal text-amber-700">Em Oficina</span></h3>
            </div>
            <div className="p-3 bg-amber-100 text-amber-700 rounded-xl border border-amber-200">
              <Wrench className="w-6 h-6" />
            </div>
          </div>
          <div className="mt-3 text-xs text-amber-800 flex items-center gap-1 border-t border-amber-200/60 pt-2 font-medium">
            <AlertTriangle className="w-3.5 h-3.5 text-amber-600" />
            Acompanhe prazos e motivos
          </div>
        </div>

        {/* Média de Consumo KM/L */}
        <div className="bg-white p-5 rounded-2xl border border-slate-200 shadow-sm relative overflow-hidden">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-xs font-bold text-slate-500 uppercase tracking-wider">Média de Consumo</p>
              <h3 className="text-2xl font-black text-slate-900 mt-1 font-mono">{mediaKmLGeral} <span className="text-xs font-normal text-slate-500">KM/L</span></h3>
            </div>
            <div className="p-3 bg-emerald-50 text-emerald-600 rounded-xl">
              <TrendingUp className="w-6 h-6" />
            </div>
          </div>
          <div className="mt-3 text-xs text-slate-600 flex items-center gap-1 border-t border-slate-100 pt-2">
            <span>Custo médio: <strong className="text-slate-800 font-mono">R$ {custoMedioKmGeral}/KM</strong></span>
          </div>
        </div>

        {/* Total Abastecimento */}
        <div className="bg-white p-5 rounded-2xl border border-slate-200 shadow-sm relative overflow-hidden">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-xs font-bold text-slate-500 uppercase tracking-wider">Total Abastecido</p>
              <h3 className="text-2xl font-black text-slate-900 mt-1 font-mono">R$ {totalGastoCombustivel.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}</h3>
            </div>
            <div className="p-3 bg-blue-50 text-blue-600 rounded-xl">
              <Fuel className="w-6 h-6" />
            </div>
          </div>
          <div className="mt-3 text-xs text-slate-600 flex items-center gap-1 border-t border-slate-100 pt-2 font-mono">
            <span>{totalLitrosAbastecidos.toFixed(1)} L em {abastecimentos.length} registros</span>
          </div>
        </div>
      </div>

      {/* Grid Secundária - Status da Frota e Agregados */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Painel de Veículos em Manutenção */}
        <div className="lg:col-span-2 bg-white rounded-2xl border border-slate-200 p-6 shadow-sm space-y-4">
          <div className="flex items-center justify-between border-b border-slate-100 pb-3">
            <div className="flex items-center gap-2">
              <div className="p-2 bg-amber-100 text-amber-800 rounded-lg">
                <Wrench className="w-5 h-5" />
              </div>
              <div>
                <h4 className="font-bold text-sm text-slate-900">Veículos em Manutenção (Frota Própria)</h4>
                <p className="text-xs text-slate-500">Relação de veículos parados em oficina</p>
              </div>
            </div>
            <span className="text-xs font-bold px-2.5 py-1 bg-amber-100 text-amber-800 rounded-full">
              {emManutencao.length} na oficina
            </span>
          </div>

          {emManutencao.length === 0 ? (
            <div className="p-8 text-center bg-slate-50 rounded-xl border border-dashed border-slate-200 text-xs text-slate-500">
              <CheckCircle2 className="w-8 h-8 text-emerald-500 mx-auto mb-2 opacity-80" />
              Toda a frota própria está operacional e disponível sem manutenções ativas!
            </div>
          ) : (
            <div className="space-y-3">
              {emManutencao.map((v) => (
                <div key={v.id} className="p-4 bg-amber-50/60 border border-amber-200 rounded-xl flex items-center justify-between flex-wrap gap-3">
                  <div className="space-y-1">
                    <div className="flex items-center gap-2">
                      <span className="px-2.5 py-0.5 bg-slate-900 text-white font-bold text-xs rounded tracking-wider">
                        {v.placa || v.id}
                      </span>
                      <span className="text-xs font-semibold text-slate-800">
                        {(v as any).modelo_rel?.modelo || (v as any).marca_rel?.marca || 'Veículo'}
                      </span>
                    </div>
                    <p className="text-xs text-amber-900 font-medium">
                      <strong>Motivo:</strong> {v.motivo_manutencao || 'Não especificado'}
                    </p>
                    {v.previsao_entrega && (
                      <p className="text-[11px] text-amber-700">
                        Previsão de saída: <strong>{new Date(v.previsao_entrega).toLocaleDateString('pt-BR')}</strong>
                      </p>
                    )}
                  </div>

                  <button
                    onClick={() => onOpenManutencao(v)}
                    className="px-3 py-1.5 bg-amber-600 hover:bg-amber-700 text-white text-xs font-bold rounded-lg transition-colors shadow-sm"
                  >
                    Atualizar Status
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Quadro comparativo: Frota Própria vs Agregados */}
        <div className="bg-white rounded-2xl border border-slate-200 p-6 shadow-sm space-y-4">
          <div className="flex items-center gap-2 border-b border-slate-100 pb-3">
            <div className="p-2 bg-slate-100 text-slate-800 rounded-lg">
              <Users className="w-5 h-5" />
            </div>
            <div>
              <h4 className="font-bold text-sm text-slate-900">Distribuição da Frota</h4>
              <p className="text-xs text-slate-500">Frota Própria vs Terceirizados</p>
            </div>
          </div>

          <div className="space-y-3 text-xs">
            {/* Frota Própria card */}
            <div className="p-3.5 bg-slate-50 rounded-xl border border-slate-200 flex items-center justify-between">
              <div>
                <span className="font-bold text-slate-900 text-sm block">Frota Própria</span>
                <span className="text-slate-500 text-[11px]">Gestão de manutenção e checklists ativa</span>
              </div>
              <span className="px-3 py-1 bg-red-600 text-white font-black rounded-lg text-sm">
                {frotaPropria.length}
              </span>
            </div>

            {/* Agregados card */}
            <div className="p-3.5 bg-slate-50 rounded-xl border border-slate-200 flex items-center justify-between">
              <div>
                <span className="font-bold text-slate-900 text-sm block">Veículos Agregados</span>
                <span className="text-slate-500 text-[11px]">Parceiros externos com repasse contratual</span>
              </div>
              <span className="px-3 py-1 bg-slate-800 text-white font-black rounded-lg text-sm">
                {agregados.length}
              </span>
            </div>

            {/* Total Geral */}
            <div className="p-3 bg-red-50 rounded-xl border border-red-100 flex items-center justify-between text-red-900 font-bold">
              <span>Total da Frota Cadastrada:</span>
              <span className="text-base">{veiculos.length} Veículos</span>
            </div>
          </div>
        </div>
      </div>

      {/* Relatórios Rápidos e Histórico Recente de Checklists */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Histórico Recente de Abastecimentos */}
        <div className="bg-white rounded-2xl border border-slate-200 p-6 shadow-sm space-y-4">
          <div className="flex items-center justify-between border-b border-slate-100 pb-3">
            <div className="flex items-center gap-2">
              <div className="p-2 bg-emerald-100 text-emerald-800 rounded-lg">
                <Fuel className="w-5 h-5" />
              </div>
              <div>
                <h4 className="font-bold text-sm text-slate-900">Últimos Abastecimentos (Frota Própria)</h4>
                <p className="text-xs text-slate-500">Média de consumo e controle de KM</p>
              </div>
            </div>
          </div>

          {abastecimentos.length === 0 ? (
            <div className="p-6 text-center text-xs text-slate-500 border border-dashed rounded-xl">
              Nenhum abastecimento registrado até o momento.
            </div>
          ) : (
            <div className="space-y-2.5">
              {abastecimentos.slice(0, 5).map((a) => (
                <div key={a.id || a.data_abastecimento} className="p-3 bg-slate-50 border border-slate-200 rounded-xl flex items-center justify-between text-xs">
                  <div>
                    <div className="font-bold text-slate-800">{a.posto_combustivel || 'Posto'} - {new Date(a.data_abastecimento).toLocaleDateString('pt-BR')}</div>
                    <div className="text-slate-500 text-[11px]">
                      KM: {a.km_atual} • {a.litros} Litros • Condutor: {a.motorista_nome || 'N/I'}
                    </div>
                  </div>
                  <div className="text-right">
                    <div className="font-bold text-emerald-700 text-xs">R$ {Number(a.valor_total).toFixed(2)}</div>
                    {a.consumo_km_l && <div className="text-[10px] text-slate-500">{a.consumo_km_l} KM/L</div>}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Resumo de Inspeções e Checklists */}
        <div className="bg-white rounded-2xl border border-slate-200 p-6 shadow-sm space-y-4">
          <div className="flex items-center justify-between border-b border-slate-100 pb-3">
            <div className="flex items-center gap-2">
              <div className="p-2 bg-blue-100 text-blue-800 rounded-lg">
                <ClipboardCheck className="w-5 h-5" />
              </div>
              <div>
                <h4 className="font-bold text-sm text-slate-900">Resumo de Checklists e Inspeções</h4>
                <p className="text-xs text-slate-500">Segurança da frota própria pré-viagem</p>
              </div>
            </div>
          </div>

          <div className="grid grid-cols-2 gap-3 text-center">
            <div className="p-4 bg-emerald-50 border border-emerald-200 rounded-xl">
              <div className="text-2xl font-black text-emerald-800 font-mono">{checklistsAprovados}</div>
              <div className="text-xs font-bold text-emerald-900 mt-1 uppercase">Aprovados</div>
            </div>
            <div className="p-4 bg-amber-50 border border-amber-200 rounded-xl">
              <div className="text-2xl font-black text-amber-800 font-mono">{checklistsAtencao}</div>
              <div className="text-xs font-bold text-amber-900 mt-1 uppercase">Observação / Alerta</div>
            </div>
          </div>

          {checklists.length === 0 ? (
            <div className="p-6 text-center text-xs text-slate-500 border border-dashed rounded-xl">
              Nenhum checklist de pré-viagem registrado ainda.
            </div>
          ) : (
            <div className="space-y-2.5">
              {checklists.slice(0, 4).map((c) => (
                <div key={c.id || c.data_checklist} className="p-3 bg-slate-50 border border-slate-200 rounded-xl flex items-center justify-between text-xs">
                  <div>
                    <div className="font-bold text-slate-800">{c.motorista_nome || 'Motorista'} - {new Date(c.data_checklist).toLocaleDateString('pt-BR')}</div>
                    <div className="text-slate-500 text-[11px]">KM Inicial: {c.km_inicial ?? 'N/I'} • Combustível: {c.combustivel_nivel}</div>
                  </div>
                  <span className={`px-2 py-0.5 rounded text-[10px] font-bold uppercase ${
                    c.status_geral === 'aprovado' ? 'bg-emerald-100 text-emerald-800' : 'bg-amber-100 text-amber-800'
                  }`}>
                    {c.status_geral.replace('_', ' ')}
                  </span>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};
